import React from 'react';
import 'twin.macro';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';
import { BasicEleveInfo } from 'types/eleveGroups';

interface Props {
  eleves: BasicEleveInfo[];
  groupId: string;
  isExpanded: boolean;
  onToggle: (group: string) => void;
  maxVisibleNames?: number;
}

export function EleveNameList({
  eleves,
  groupId,
  isExpanded,
  onToggle,
  maxVisibleNames = 3,
}: Props) {
  const { t } = useTranslation(['cahierJournal']);

  if ((eleves || []).length === 0)
    return <span>{t('ateliers.elevesGroup.noStudents')}</span>;

  if (isExpanded) {
    return (
      <div tw="flex items-center space-x-2">
        <span tw="text-sm text-neutral-quiet">
          {eleves.length}{' '}
          {t('ateliers.elevesGroup.students', { count: eleves.length })}
        </span>
        <button
          onClick={() => onToggle(groupId)}
          tw="p-1 rounded-full hover:bg-neutral-supershy"
        >
          <XMarkIcon tw="h-4 w-4 text-neutral-quiet" />
        </button>
      </div>
    );
  }

  const getVisibleEleves = () => {
    let totalLength = 0;
    return eleves.filter((eleve, index) => {
      const nameLength =
        eleve.givenName.length + (index < eleves.length - 1 ? 2 : 0); // +2 for ', '
      totalLength += nameLength;
      return totalLength <= maxVisibleNames * 6; // assuming average character width
    });
  };

  const visibleEleves = getVisibleEleves();
  const remainingCount = eleves.length - visibleEleves.length;

  return (
    <div tw="flex items-center space-x-1 overflow-hidden">
      {visibleEleves.map((eleve, index) => (
        <span key={eleve.id} tw="truncate">
          {eleve.givenName}
          {index < visibleEleves.length - 1 ? ', ' : ''}
        </span>
      ))}
      {remainingCount > 0 && (
        <span
          tw="ml-1 text-xs text-neutral-quiet bg-neutral-supershy rounded-full px-1.5 py-0.5 hover:bg-brand-quiet shrink-0 cursor-pointer"
          onClick={e => {
            e.stopPropagation();
            onToggle(groupId);
          }}
        >
          +{remainingCount}
        </span>
      )}
    </div>
  );
}
